import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
    Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
    AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem,
    Divider, useMediaQuery, useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import briefcaseFill from '@iconify/icons-eva/briefcase-fill';
import personFill from '@iconify/icons-eva/person-fill';
import logOutFill from '@iconify/icons-eva/log-out-fill';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';

const DRAWER_WIDTH = 260;

const RootStyle = styled('div')({
    display: 'flex',
    minHeight: '100vh',
});

const MainStyle = styled('main')(({ theme }) => ({
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100vh',
    paddingTop: 72,
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        marginLeft: DRAWER_WIDTH, // Account for permanent drawer
    },
}));

const NAV_ITEMS = [
    { title: 'My Deals', path: '/founder/deals', icon: briefcaseFill },
    { title: 'Profile', path: '/founder/profile', icon: personFill },
];

export default function FounderLayout() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const user = JSON.parse(localStorage.getItem('founderUser') || '{}');

    const handleLogout = () => {
        localStorage.removeItem('founderAuthToken');
        localStorage.removeItem('founderUser');
        navigate('/founder/login');
    };

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>GX</Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Gateway X</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Founder Portal</Typography>
                </Box>
            </Box>
            <Divider />
            <List sx={{ px: 1.5, pt: 2, flex: 1 }}>
                {NAV_ITEMS.map((item) => (
                    <ListItemButton
                        key={item.path}
                        onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                        sx={{
                            borderRadius: 1.5, mb: 0.5, py: 1,
                            ...(location.pathname.startsWith(item.path) && {
                                bgcolor: 'action.selected',
                                color: 'primary.main',
                                '& .MuiListItemIcon-root': { color: 'primary.main' },
                            }),
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Icon icon={item.icon} width={22} height={22} />
                        </ListItemIcon>
                        <ListItemText primary={item.title} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );

    return (
        <RootStyle>
            <AppBar position="fixed" sx={{
                bgcolor: '#fff', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: `${DRAWER_WIDTH}px` },
            }}>
                <Toolbar>
                    {isMobile && (
                        <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                            <Icon icon={menu2Fill} width={24} height={24} />
                        </IconButton>
                    )}
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
                        {NAV_ITEMS.find(i => location.pathname.startsWith(i.path))?.title || 'Founder Portal'}
                    </Typography>
                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: '#1a237e', fontSize: 14 }}>
                            {(user.first_name || 'F')[0]}
                        </Avatar>
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
                        PaperProps={{ sx: { width: 200, mt: 1 } }}>
                        <MenuItem disabled>
                            <Typography variant="body2" noWrap>{user.email || 'Founder'}</Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/founder/profile'); }}>
                            <ListItemIcon><Icon icon={personFill} width={20} /></ListItemIcon>
                            Profile
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }}>
                            <ListItemIcon><Icon icon={logOutFill} width={20} /></ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {isMobile ? (
                <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}
                    PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
                    {drawerContent}
                </Drawer>
            ) : (
                <Drawer variant="permanent" PaperProps={{ sx: { width: DRAWER_WIDTH, borderRight: '1px solid', borderColor: 'divider' } }}>
                    {drawerContent}
                </Drawer>
            )}

            <MainStyle>
                <Outlet />
            </MainStyle>
        </RootStyle>
    );
}
